/**
 * Signup Page Component
 *
 * Public page that allows new users to self-register.
 * Requires a special signup code which is validated server-side only.
 * After successful signup the user is automatically logged in.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { designTokens } from '@course-authoring/shared-types';
import { useSignupMutation, type SignupPayload, type UserApiError } from '../api/userApi';

function signupErrorMessage(error: UserApiError): string {
  if (error.status === 0) {
    return 'Account creation could not be confirmed because of a connection problem. Check your connection and try signing in before submitting again.';
  }
  if (error.status === 403) return 'Invalid signup code. Check the code and try again.';
  if (error.status === 409) return 'An account with this email already exists. Sign in instead.';
  if (error.status === 429) {
    const seconds = error.retryAfterSeconds;
    if (seconds !== undefined && Number.isFinite(seconds) && seconds >= 0) {
      if (seconds === 0) return 'Too many signup requests. You can try again now.';
      const minutes = Math.ceil(seconds / 60);
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const remainder = minutes % 60;
        const extraMinutes = remainder ? ` ${remainder} minute${remainder === 1 ? '' : 's'}` : '';
        return `Too many signup requests. Try again in about ${hours} hour${hours === 1 ? '' : 's'}${extraMinutes}.`;
      }
      const amount = Math.ceil(seconds < 60 ? seconds : seconds / 60);
      const unit = seconds < 60 ? 'second' : 'minute';
      return `Too many signup requests. Try again in about ${amount} ${unit}${amount === 1 ? '' : 's'}.`;
    }
    return 'Too many signup requests. Please try again later.';
  }
  if (error.status === 400) return error.message || 'Check your account details and try again.';
  return 'Account creation could not be completed. Please try again later.';
}

export function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupCode, setSignupCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { mutate: doSignup, isPending } = useSignupMutation({
    onSuccess: () => {
      // Cookies are set by the server — a full reload lets AuthProvider hydrate the new session
      window.location.href = '/lessons';
    },
    onError: (err) => {
      setError(signupErrorMessage(err));
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isPending) return;
    setError(null);

    if (!signupCode.trim()) {
      setError('Signup code is required.');
      return;
    }

    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    const payload: SignupPayload = {
      name: name.trim(),
      email: email.trim(),
      password,
      signupCode: signupCode.trim(),
    };

    doSignup(payload);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Create Account</h1>
        <p style={styles.subtitle}>Enter your details and signup code</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && (
            <div style={styles.errorBox} role="alert">
              {error}
            </div>
          )}

          <div style={styles.field}>
            <label htmlFor="signup-name" style={styles.label}>
              Full Name
            </label>
            <input
              id="signup-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isPending}
              style={styles.input}
              autoComplete="name"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="signup-email" style={styles.label}>
              Email
            </label>
            <input
              id="signup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isPending}
              style={styles.input}
              autoComplete="email"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="signup-password" style={styles.label}>
              Password
            </label>
            <input
              id="signup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={12}
              disabled={isPending}
              style={styles.input}
              autoComplete="new-password"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="signup-confirm-password" style={styles.label}>
              Confirm Password
            </label>
            <input
              id="signup-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={12}
              disabled={isPending}
              style={styles.input}
              autoComplete="new-password"
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="signup-code" style={styles.label}>
              Signup Code
            </label>
            <input
              id="signup-code"
              type="text"
              value={signupCode}
              onChange={(e) => setSignupCode(e.target.value)}
              required
              disabled={isPending}
              style={styles.input}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={isPending}
            style={{
              ...styles.button,
              opacity: isPending ? 0.7 : 1,
              cursor: isPending ? 'not-allowed' : 'pointer',
            }}
          >
            {isPending ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <p style={styles.footerText}>
          Already have an account?{' '}
          <Link to="/login" style={styles.link}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: designTokens.colors.canvasBackground,
    padding: '16px',
  },
  card: {
    backgroundColor: designTokens.colors.inspectorBackground,
    borderRadius: designTokens.borders.blockRadius,
    boxShadow: designTokens.shadows.blockShadow,
    padding: '32px',
    width: '100%',
    maxWidth: '420px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: designTokens.colors.textPrimary,
    textAlign: 'center' as const,
  },
  subtitle: {
    margin: '8px 0 24px',
    fontSize: '14px',
    color: designTokens.colors.textSecondary,
    textAlign: 'center' as const,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '14px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 500,
    color: designTokens.colors.textPrimary,
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: `1px solid ${designTokens.colors.textMuted}`,
    borderRadius: '6px',
    outline: 'none',
    transition: designTokens.transitions.default,
    backgroundColor: 'transparent',
    color: designTokens.colors.textPrimary,
  },
  errorBox: {
    padding: '12px',
    backgroundColor: designTokens.colors.errorBackground,
    border: `1px solid ${designTokens.colors.errorBorder}`,
    borderRadius: '6px',
    color: designTokens.colors.errorText,
    fontSize: '14px',
  },
  button: {
    padding: '12px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: designTokens.colors.selectionBorder,
    border: 'none',
    borderRadius: '6px',
    marginTop: '8px',
    transition: designTokens.transitions.default,
  },
  footerText: {
    marginTop: '20px',
    fontSize: '13px',
    color: designTokens.colors.textSecondary,
    textAlign: 'center' as const,
  },
  link: {
    color: '#4183C4',
    fontWeight: 'normal',
    textDecoration: 'underline',
  },
};
